import $ from "jquery";

import * as cardsHelpers from "./cards";
import highchartsExtras from "./util/highcharts_extras";
import HighchartsReact from "highcharts-react-official";
window.HighchartsReact = HighchartsReact;

window.$ = window.jQuery = $; // Hack — set before Highcharts loads

// Highcharts 12+ changed module loading: the module files are no longer callable
// factories — each reads the shared core off `window._Highcharts` (which the CORE bundle
// publishes when it loads) and extends it as a side effect AT REQUIRE TIME. So core MUST
// be required FIRST, then each module (no more `Module(Highcharts)` calls). Using
// `require` (not hoisted `import`) keeps statement order: jQuery global -> core -> modules.
// (Pre-13 this file imported the modules before core; under 13 that throws at load —
//  `window._Highcharts` is undefined — poisoning the whole global bundle.)
const Highcharts = require("highcharts/highcharts.src");
require("highcharts/highcharts-more.src");
require("highcharts/modules/treemap.src");
require("highcharts/modules/pattern-fill.src");
require("highcharts/modules/exporting.src");
require("highcharts/modules/offline-exporting.src");
require("highcharts/modules/broken-axis.src");
require("highcharts/modules/map");

// Highcharts 13 enables accessibility by default and console.warns on every chart when
// the accessibility module isn't loaded. We don't ship that module (it would add a11y
// DOM not present in the pre-upgrade build) — keep the prior behaviour and silence the
// warning by explicitly disabling a11y. (Revisit as a deliberate a11y feature later.)
//
// Highcharts 13 also added a palette/colorScheme layer that adapts colors to the browser's
// prefers-color-scheme: chart.backgroundColor, legend text, etc. default to `light-dark(...)`
// CSS vars (default-on in non-styled mode since v12.3) that resolve to dark values on a
// dark-mode OS (Chrome 123+). v7 used static light colors. We render light UI only, so pin a
// LIGHT color scheme in the single global setOptions call below — the one configuration point
// every chart inherits from, chartkick + React + direct — so no dark-mode client gets dark charts.
//
// The colorScheme option lives under `palette.colorScheme`, NOT at the
// top level. setOptions merges a top-level `colorScheme` into defaultOptions.colorScheme,
// which Palette.update() never reads (it reads defaultOptions.palette.colorScheme) — so an
// earlier top-level `colorScheme:"light"` was silently ignored and only the explicit backgroundColor
// pin held. Legend text then washed out / went invisible on hover for dark-mode clients.
// Nesting it under `palette` makes Palette.update() set `color-scheme: light` on each chart
// container, forcing every light-dark() var to its light value. We ALSO pin the chart
// background and the legend item text colors to explicit light-mode hex (belt-and-suspenders:
// explicit colors survive regardless of CSS-var resolution), and pin tooltip width to "auto"
// to opt out of HC13's new chart-width tooltip constraint (Tooltip.js forces
// width = chart.spacingBox.width when tooltip.style.width is unset — added in v13 for issue
// #24104 — which made our useHTML tooltips balloon to full card width).
Highcharts.setOptions({
  accessibility: { enabled: false },
  palette: { colorScheme: "light" },
  chart: { backgroundColor: "#FFFFFF" },
  // HC10+ replaced HC7's simple treemap "< Back" traverse-up button with a "breadcrumbs"
  // control that, by default (showFullPath:true), renders a full "Series 1 / Other" drill PATH — a
  // stray crumb prod (HC7) never showed. The treemap builds its breadcrumbs instance from
  // `merge(series.drillUpButton, series.breadcrumbs)`, and the Breadcrumbs constructor merges
  // `chart.options.navigation.breadcrumbs` over its defaults — so `navigation` is the one
  // configuration point that reaches every treemap. The treemap templates do not all share a
  // parent (several extend TreemapChart, RegionsGlobalWaterMarketsTreeMap extends ChartTemplate
  // directly), so fixing this template by template would mean repeating it in each and missing
  // any new one. `showFullPath:false` routes render() to renderSingleButton(), which draws ONE
  // button (auto-hidden at root, shown at level>0) — exactly prod's HC7 back button — and
  // `format:'< Back'` pins its label. An earlier fix no-op'd Breadcrumbs.render() wholesale to
  // hide the trail, which also removed the back button; this configures showFullPath instead,
  // which render() reads — restoring the drill-up nav.
  // (Note: `breadcrumbs:{enabled:false}` is ignored wherever it is set — HC13's Breadcrumbs never
  // checks an `enabled` option — which is why the earlier fix reached for the render wrap in the
  // first place.)
  // HC13's single-button defaults draw a bare highlight-blue text link
  // top-LEFT above the plot; prod (HC7 renderTraverseUpButton) drew a BOXED renderer.button —
  // #f7f7f7 fill / #cccccc 1px stroke / #333 text / r 2 / padding 8, hover #e6e6e6 — floated
  // INSIDE the plot top-RIGHT (align right, x -10, y 10, relativeTo plotBox, no layout space
  // reserved). Prod's label read "< Back" (its node-name fallback), which `format`
  // already pins. Theme values lifted from the HC 7.2.2 source (SVGRenderer.button defaults +
  // treemap traverseUpButton.position); floating:true stops HC13 shifting the plot down to make
  // room, matching HC7's overlay behaviour. relativeTo already defaults to plotBox.
  navigation: { breadcrumbs: {
    showFullPath: false,
    format: "< Back",
    floating: true,
    position: { align: "right", verticalAlign: "top", x: -10, y: 10 },
    buttonTheme: {
      fill: "#f7f7f7",
      stroke: "#cccccc",
      "stroke-width": 1,
      r: 2,
      padding: 8,
      style: { color: "#333333", fontWeight: "normal" },
      states: {
        hover: { fill: "#e6e6e6" },
        select: { fill: "#e6ebf5", style: { color: "#000000" } }
      }
    }
  } },
  legend: {
    itemStyle: { color: "#333333" },        // --highcharts-neutral-color-80 light value
    itemHoverStyle: { color: "#000000" },    // --highcharts-neutral-color-100 light value
    itemHiddenStyle: { color: "#666666", textDecoration: "line-through" } // -60 light value
  },
  tooltip: { style: { width: "auto" } }
});

// Report a throw out of a chart redraw or load to Airbrake with the card it came from,
// then re-raise so nothing is swallowed. A chart that fails to draw is otherwise silent:
// the container is simply empty, and no one hears about it until a reader complains.
// Not Highcharts-version-specific — it reports whatever the chart throws — and the
// notifier itself lives in util/highcharts_extras.js, which the legend-click path also uses.
function wrapChartMethodWithErrorReporting(methodName) {
  Highcharts.wrap(Highcharts.Chart.prototype, methodName, function (proceed) {
    const chart = this;

    try {
      return proceed.apply(chart, Array.prototype.slice.call(arguments, 1));
    } catch (error) {
      highchartsExtras.notifyChartError(chart, error, methodName);
      throw error;
    }
  });
}

wrapChartMethodWithErrorReporting("redraw");
wrapChartMethodWithErrorReporting("onload");

// Plugin to add support for credits.target in Highcharts.
Highcharts.wrap(Highcharts.Chart.prototype, "showCredits", function (proceed, credits) {
  proceed.call(this, credits);

  if (credits && credits.enabled && this.credits) {
    this.credits.element.onclick = function () {

      // dynamically create an anchor element and click it
      // use the settings defined in highcharts (credits.target)

      const link = document.createElement("a");
      link.href = credits.href;
      link.target = credits.target;
      window.Navigation.goToLink($(link));
    };
  }
});

/**
 * Extend the Axis.getLinePath method in order to visualize breaks with two parallel
 * slanted lines. For each break, the slanted lines are inserted into the line path.
 *
 * Highcharts 10+ emits a NESTED SVGPath (array of [cmd, ...coords] segments) instead of
 * the old flat ["M", x, y, "L", ...] array: the initial moveTo is path[0], and break
 * segments are spliced in after it (index 1, was flat index 3). `Highcharts.each` was
 * removed in v8 -> native Array#forEach.
 */
Highcharts.wrap(Highcharts.Axis.prototype, "getLinePath", function (proceed, lineWidth) {
  const axis = this;
  const path = proceed.call(this, lineWidth);
  const start = path[0] || [];
  let x = start[1];
  let y = start[2];

  (this.breakArray || []).forEach(function (brk) {
    let segments;
    if (axis.horiz) {
      x = axis.toPixels(brk.from);
      segments = [
        ["L", x - 4, y], // stop
        ["M", x - 9, y + 5], ["L", x + 1, y - 5], // left slanted line
        ["M", x - 1, y + 5], ["L", x + 9, y - 5], // higher slanted line
        ["M", x + 4, y]
      ];
    } else {
      y = axis.toPixels(brk.from);
      segments = [
        ["L", x, y - 4], // stop
        ["M", x + 5, y - 9], ["L", x - 5, y + 1], // lower slanted line
        ["M", x + 5, y - 1], ["L", x - 5, y + 9], // higher slanted line
        ["M", x, y + 4]
      ];
    }
    path.splice(1, 0, ...segments);
  });
  return path;
});
/**
 * On top of each column, draw a zigzag line where the axis break is.
 */
highchartsExtras.pointBreakColumn = function (e) {
  const point = e.point,
    brk = e.brk,
    shapeArgs = point.shapeArgs,
    x = shapeArgs.x,
    y = this.translate(brk.from, 0, 1, 0, 1),
    w = shapeArgs.width,
    key = ["brk", brk.from, brk.to],
    path = ["M", x, y, "L", x + w * 0.25, y + 4, "L", x + w * 0.75, y - 4, "L", x + w, y];

  if (!point[key]) {
    point[key] = this.chart.renderer.path(path)
      .attr({
        "stroke-width": 2,
        stroke: "white"
      })
      .add(point.graphic.parentGroup);
  } else {
    point[key].attr({
      d: path
    });
  }
};
highchartsExtras.cardResizedAdjustVisualization = cardsHelpers.cardResizedAdjustVisualization;
highchartsExtras.chartsFromCard = cardsHelpers.chartsFromCard;

Highcharts.SVGRenderer.prototype.symbols.addToSlideDeck = function (x, y, w, h) {
  return ["M", x, y + h / 2, "L", x + w, y + h / 2, "M", x + w / 2, y, "L", x + w / 2, y + h, "z"];
};

// Drop-in fix for Highcharts issue #8477 on older Highcharts versions. The
// issue is fixed since Highcharts v6.1.1.
// Highcharts.wrap(Highcharts.Axis.prototype, "getPlotLinePath", function(proceed) {
//   const path = proceed.apply(this, Array.prototype.slice.call(arguments, 1));
//   path.flat = false;
//   return path;
// });

window.Highcharts = Highcharts;

window.highchartsExtras = highchartsExtras;

